import * as z from "zod";
import { gql } from "graphql-request";
import { SysSchema, SysFragment } from "../sys";
import { ImageSchema, ImageFragment } from "../image";
import { UnifiedButtonSchema, UnifiedButtonFragment } from "../unifiedButton";
import { ButtonTargetSchema } from "../buttonTarget";

export const CalendarEventRewardSchema = z.object({
  sys: SysSchema,
  label: z.string().nullable(),
  image: ImageSchema.nullable().optional(),
});

export type CalendarEventReward = z.infer<typeof CalendarEventRewardSchema>;

export const CalendarEventEntrySchema = z.object({
  sys: SysSchema,
  label: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  image: ImageSchema.nullable().optional(),
  target: ButtonTargetSchema,
});

export type CalendarEventEntry = z.infer<typeof CalendarEventEntrySchema>;

export const EventCalendarEntrySchema = z.object({
  __typename: z.literal("EventCalendarEntry"),
  sys: SysSchema,
  entryTitle: z.string().nullable(),

  ctaButtonsCollection: z
    .object({
      items: z.array(UnifiedButtonSchema).nullish(),
    })
    .nullish(),

  backgroundImage: ImageSchema.nullable(),
  backgroundImageEn: ImageSchema.nullable(),

  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  barColor: z.string().nullable(),
  textColor: z.string().nullable(),
  showProgressBar: z.boolean().nullable(),
  showEventLabels: z.boolean().nullable(),
  dateAppearance: z.enum(["none", "regular", "range"]).nullable(),

  calendarEventsCollection: z
    .object({
      items: z.array(CalendarEventEntrySchema).nullish(),
    })
    .nullish(),

  rewardsTitle: z.string().nullable(),
  rewardsCollection: z
    .object({
      items: z.array(CalendarEventRewardSchema).nullish(),
    })
    .nullish(),
});

export type EventCalendarEntry = z.infer<typeof EventCalendarEntrySchema>;

export const EventCalendarEntryFragment = gql`
  fragment EventCalendarEntryFragment on EventCalendarEntry {
    __typename
    sys {
      ...SysFragment
    }

    ctaButtonsCollection {
      items {
        __typename
        ... on UnifiedButton {
          ...UnifiedButtonFragment
        }
      }
    }

    entryTitle

    backgroundImage {
      ...ImageFragment
    }
    backgroundImageEn: backgroundImage(locale: "en") {
      ...ImageFragment
    }

    startDate
    endDate
    barColor
    textColor
    showProgressBar
    showEventLabels
    dateAppearance

    calendarEventsCollection(limit: 50, locale: "en") {
      items {
        __typename
        sys {
          ...SysFragment
        }
        label
        startDate
        endDate
        image {
          ...ImageFragment
        }
        target
      }
    }

    rewardsTitle
    rewardsCollection(limit: 20, locale: "en") {
      items {
        __typename
        sys {
          ...SysFragment
        }
        label
        image {
          ...ImageFragment
        }
      }
    }
  }

  ${SysFragment}
  ${ImageFragment}
  ${UnifiedButtonFragment}
`;
